import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { doc, getDoc, getFirestore, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import axios from 'axios';
import Skeleton from '@mui/material/Skeleton';
import Iconify from '../components/iconify';
import firebaseApp from '../utils/firebaseConfig';
import { pickBestMember, isSamePerson } from '../utils/members';
import { stripAccents } from '../utils/search';
import { isPrivateCourse, COURSE_LANGUAGE_OPTIONS, LICENSE_TYPE_OPTIONS, privateCourseSummary } from '../utils/privateCourse';
import {
  money,
  partnerBillingOf,
  partnerPenaltyApplies,
  partnerPenaltyCentsOf,
  partnerRentalReason,
  PARTNER_CHANGE_HOURS,
} from '../utils/partners';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

// Requisitos del curso — mismo orden que las columnas de la hoja impresa.
// calBalas y multas son de texto: lo escrito sale impreso en la celda (ej: "9MM", "$50").
const COURSE_CHECKS = [
  { key: 'abog', label: 'Abog' },
  { key: 'comp100', label: 'Comp 100' },
  { key: 'comp200', label: 'Comp 200' },
  { key: 'foto', label: 'Foto' },
  { key: 'abog5', label: 'Abog 5 o más' },
  { key: 'calBalas', label: 'Cal. Balas', input: true, placeholder: 'Ej: 9MM' },
  { key: 'multas', label: 'Multas', input: true, placeholder: 'Ej: 50', money: true },
];

// Timestamp de Firestore, ISO string o Date → Date (o null)
const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleString('es-PR', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
};

const fmtDate = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleDateString('es-PR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
};

const statusColors = {
  confirmed: 'success',
  pending_payment: 'warning',
  completed: 'info',
  cancelled: 'default',
  refunded: 'secondary',
  expired: 'default',
  no_show: 'error',
};

const CATEGORY_TYPE_LABELS = {
  lane: 'Lane de Tiro',
  course: 'Curso',
  class: 'Clase',
  event: 'Evento',
  'one-on-one': 'One on One',
  'no-license-shooting': 'Tiro Sin Licencia',
};

export default function ReservationDetailsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get('id');

  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [issueRefund, setIssueRefund] = useState(true);
  // Inscripción de armería cancelada con menos de 24 h: ¿se le cobra la
  // penalidad? (se apaga si la cancelación es por parte de BBTC)
  const [partnerPenaltyOnCancel, setPartnerPenaltyOnCancel] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Reschedule
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  // Duplicar (pedido 2026-10-07): mismo diálogo, pero en vez de mover la
  // reservación se crea OTRA igual (mismo cliente, categoría, calibres, notas)
  // en la fecha/hora escogida. Si el lane actual está libre a esa hora se
  // usa; si no, se escoge automáticamente otro lane disponible.
  const [duplicateMode, setDuplicateMode] = useState(false);
  // Cambio de lane a OTRA categoría de lane (pistola ⇄ rifle, pedido Lola
  // 2026-10-19). '' = la categoría actual de la reservación.
  const [newCategoryId, setNewCategoryId] = useState('');
  const [laneCategories, setLaneCategories] = useState([]);
  const [newDate, setNewDate] = useState('');
  // Lista de fechas disponibles (según availability_rules de la categoría)
  const [availableDates, setAvailableDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [newSlots, setNewSlots] = useState([]);
  const [newSlotsLoading, setNewSlotsLoading] = useState(false);
  const [newStartTime, setNewStartTime] = useState('');
  const [rescheduleError, setRescheduleError] = useState(null);

  // Lane dentro del MISMO diálogo de cambio (pedido 2026-09-15: antes fecha/
  // hora y lane eran dos diálogos, y si el lane actual no estaba libre en el
  // nuevo horario no dejaba mover la reservación).
  const [laneOptions, setLaneOptions] = useState([]);
  const [lanesLoading, setLanesLoading] = useState(false);
  const [newLaneId, setNewLaneId] = useState('');
  const [laneError, setLaneError] = useState(null);

  // Acompañante: editar datos o quitarlo (ej: dato mal escrito, o no llegó)
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({ name: '', phone: '', email: '', licenseNumber: '' });
  const [guestError, setGuestError] = useState(null);

  useEffect(() => {
    if (reservationId) loadReservation();
  }, [reservationId]);

  const [reservationCategory, setReservationCategory] = useState(null);

  // Membresía del cliente (pedido 2026-09-29): la reservación de un miembro
  // sale en $0 / free, y el staff no veía que el cliente acababa de pagar la
  // membresía. Se busca su member y las compras pagadas en membership_requests
  // (ahí caen la tienda web/app, las aprobaciones del staff y las renovaciones
  // automáticas).
  const [membership, setMembership] = useState(null); // { member, purchases[] }

  useEffect(() => {
    if (!reservation?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const member = await findReservationMember(reservation);
        if (!member) { if (!cancelled) setMembership(null); return; }
        const snap = await getDocs(query(collection(db, 'membership_requests'), where('memberId', '==', member.id)));
        const purchases = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.status === 'approved' && p.paymentIntentId && p.requestType !== 'cancel')
          .sort((a, b) => toDate(b.decidedAt || b.createdAt) - toDate(a.decidedAt || a.createdAt));
        if (!cancelled) setMembership({ member, purchases });
      } catch (e) {
        console.error('Error loading membership:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation?.id, reservation?.userPhone, reservation?.userEmail]);

  const loadReservation = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'reservations', reservationId));
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Convert Timestamps
        if (data.createdAt?.toDate) data.createdAt = data.createdAt.toDate().toISOString();
        if (data.updatedAt?.toDate) data.updatedAt = data.updatedAt.toDate().toISOString();
        if (data.cancelledAt?.toDate) data.cancelledAt = data.cancelledAt.toDate().toISOString();
        if (data.expiresAt?.toDate) data.expiresAt = data.expiresAt.toDate().toISOString();
        setReservation({ id: docSnap.id, ...data });
        setNotesDraft(data.notes || '');
        // Categoría: para saber si el curso es privado (switch privateCourse)
        if (data.categoryId) {
          getDoc(doc(db, 'reservation_categories', data.categoryId))
            .then((c) => setReservationCategory(c.exists() ? { id: c.id, ...c.data() } : null))
            .catch(() => setReservationCategory(null));
        }
      }
    } catch (error) {
      console.error('Error loading reservation:', error);
    }
    setLoading(false);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  // Requisitos del curso: se marcan aquí y salen con ✕ en la hoja impresa
  // Curso privado: idioma (es/en) y nueva/renovación — se guardan al instante.
  // Tocar la opción ya marcada la quita (null).
  const savePrivateCourseField = async (key, value) => {
    const next = reservation && reservation[key] === value ? null : value;
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        [key]: next,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({ ...prev, [key]: next }));
    } catch (e) {
      console.error(e);
      alert('Error guardando el dato del curso.');
    }
  };

  // Quita idioma y tipo de licencia de una reservación de curso regular
  const clearPrivateCourseFields = async () => {
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        courseLanguage: null,
        licenseType: null,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({ ...prev, courseLanguage: null, licenseType: null }));
    } catch (e) {
      console.error(e);
      alert('Error quitando el dato del curso.');
    }
  };

  const toggleCourseCheck = async (key, checked) => {
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        [`courseChecks.${key}`]: checked,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({
        ...prev,
        courseChecks: { ...(prev.courseChecks || {}), [key]: checked },
      }));
    } catch (e) {
      console.error(e);
      alert('Error guardando el requisito.');
    }
  };

  // Requisitos de texto (Cal. Balas, Multas): lo escrito sale impreso en la celda
  const saveCourseCheckText = async (key, value) => {
    const trimmed = value.trim();
    const current = reservation.courseChecks?.[key];
    if ((typeof current === 'string' ? current : '') === trimmed) return;
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        [`courseChecks.${key}`]: trimmed,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({
        ...prev,
        courseChecks: { ...(prev.courseChecks || {}), [key]: trimmed },
      }));
    } catch (e) {
      console.error(e);
      alert('Error guardando el requisito.');
    }
  };

  // Campos del cliente editables inline (name, email, phone, licencia, member id)
  const [editField, setEditField] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  // Notas internas de la reservación
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  const handleSaveNotes = async () => {
    setNotesSaving(true);
    setNotesSaved(false);
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        notes: notesDraft.trim() || null,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({ ...prev, notes: notesDraft.trim() || null }));
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (e) {
      console.error(e);
      alert('Error guardando la nota.');
    }
    setNotesSaving(false);
  };

  // Personas adicionales del registro del range — el staff las añade aquí y
  // salen en el reporte del registro del campo de tiro
  const [attendeeForm, setAttendeeForm] = useState({ phone: '', name: '', licenseNumber: '', calibers: '' });
  const [attendeeLookup, setAttendeeLookup] = useState(null); // null | 'searching' | 'found' | 'not_found'
  const [attendeeSaving, setAttendeeSaving] = useState(false);

  // Se busca por TELÉFONO (así se identifica a los clientes en reservaciones)
  const lookupAttendeeByPhone = async () => {
    const digits = (attendeeForm.phone || '').replace(/\D/g, '');
    if (digits.length < 7) return;
    setAttendeeLookup('searching');
    try {
      const snap = await getDocs(query(collection(db, 'members'), where('phone', '==', digits)));
      if (!snap.empty) {
        const m = snap.docs[0].data();
        setAttendeeForm((prev) => ({
          ...prev,
          name: prev.name || m.fullName || '',
          licenseNumber: prev.licenseNumber || m.licenseNumber || m.gunLicense || '',
        }));
        setAttendeeLookup('found');
      } else {
        setAttendeeLookup('not_found');
      }
    } catch (e) {
      console.error(e);
      setAttendeeLookup(null);
    }
  };

  const saveAttendees = async (list) => {
    await updateDoc(doc(db, 'reservations', reservationId), {
      attendees: list,
      updatedAt: new Date(),
    });
    setReservation((prev) => ({ ...prev, attendees: list }));
  };

  // En reservaciones de tiro el calibre es obligatorio para cada persona
  // (registro del campo de tiro)
  const reservationAsksCaliber = reservation && ['lane', 'no-license-shooting'].includes(reservation.categoryType);

  const handleAddAttendee = async () => {
    if (!attendeeForm.name.trim()) return;
    if (reservationAsksCaliber && !attendeeForm.calibers.trim()) {
      alert('Indica el calibre que va a usar esta persona (requerido para el registro del campo de tiro).');
      return;
    }
    setAttendeeSaving(true);
    try {
      const entry = {
        name: attendeeForm.name.trim(),
        phone: (attendeeForm.phone || '').replace(/\D/g, '') || null,
        licenseNumber: attendeeForm.licenseNumber.trim() || null,
        calibers: attendeeForm.calibers
          ? attendeeForm.calibers.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        addedAt: new Date().toISOString(),
      };
      await saveAttendees([...(reservation.attendees || []), entry]);
      setAttendeeForm({ phone: '', name: '', licenseNumber: '', calibers: '' });
      setAttendeeLookup(null);
    } catch (e) {
      console.error(e);
      alert('Error añadiendo la persona.');
    }
    setAttendeeSaving(false);
  };

  const handleRemoveAttendee = async (idx) => {
    try {
      await saveAttendees((reservation.attendees || []).filter((_, i) => i !== idx));
    } catch (e) {
      console.error(e);
      alert('Error eliminando la persona.');
    }
  };

  const saveEditField = async () => {
    const key = editField;
    setEditField(null);
    if (!key) return;
    let trimmed = editDraft.trim();
    if (key === 'userEmail') trimmed = trimmed.toLowerCase();
    // calibers se edita como texto "9MM, 45 ACP" pero se guarda como lista
    if (key === 'calibers') {
      const list = trimmed ? trimmed.split(',').map((s) => s.trim()).filter(Boolean) : [];
      if (list.join('|') === (reservation.calibers || []).join('|')) return;
      if (reservationAsksCaliber && list.length === 0) {
        alert('El calibre es requerido en reservaciones de tiro — no se puede dejar vacío.');
        return;
      }
      try {
        await updateDoc(doc(db, 'reservations', reservationId), { calibers: list, updatedAt: new Date() });
        setReservation((prev) => ({ ...prev, calibers: list }));
      } catch (e) {
        console.error(e);
        alert('Error guardando el campo.');
      }
      return;
    }
    const current = reservation[key] || '';
    if (trimmed === current) return;
    if (key === 'userName' && !trimmed) return; // el nombre no puede quedar vacío
    let value = trimmed || null;
    if (key === 'userEmail' && value) value = value.toLowerCase();
    if (key === 'userPhone' && value) value = value.replace(/\D/g, '');
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        [key]: value,
        ...(key === 'userPhone' ? { userPhoneDigits: value } : {}),
        updatedAt: new Date(),
      });
      setReservation((prev) => ({ ...prev, [key]: value }));
    } catch (e) {
      console.error(e);
      alert('Error guardando el campo.');
      return;
    }
    // El mismo cambio se aplica al récord del cliente en `members` (pedido
    // 2026-09-15: antes solo cambiaba en la reservación y el usuario quedaba
    // con el dato viejo).
    try {
      await syncMemberField(key, value);
    } catch (e) {
      console.error(e);
      alert('El dato se guardó en la reservación, pero no se pudo actualizar el récord del cliente.');
    }
  };

  // Busca el member de esta reservación (por los datos que tenía ANTES del
  // cambio) y le escribe el mismo campo. Con cuentas duplicadas se usa
  // pickBestMember + isSamePerson para no pisarle el dato a otra persona que
  // comparta teléfono.
  const findReservationMember = async (res) => {
    const email = String(res.userEmail || '').trim().toLowerCase();
    const phone = String(res.userPhone || '').replace(/\D/g, '');
    if (!email && !phone) return null;
    const queries = [];
    if (email) queries.push(getDocs(query(collection(db, 'members'), where('email', '==', email))));
    if (phone) {
      queries.push(getDocs(query(collection(db, 'members'), where('phone', '==', phone))));
      if (res.userPhone && res.userPhone !== phone) {
        queries.push(getDocs(query(collection(db, 'members'), where('phone', '==', res.userPhone))));
      }
    }
    const snaps = await Promise.all(queries);
    const seen = new Map();
    snaps.forEach((snap) => snap.docs.forEach((d) => { if (!seen.has(d.id)) seen.set(d.id, d); }));
    const member = pickBestMember([...seen.values()], { email, phone });
    if (!member) return null;
    if (!isSamePerson(member, { email, fullName: res.userName })) return null;
    return member;
  };

  const syncMemberField = async (key, value) => {
    const fieldMap = { userName: 'fullName', userEmail: 'email', userPhone: 'phone', licenseNumber: 'licenseNumber', memberId: 'memberId' };
    const memberField = fieldMap[key];
    if (!memberField) return;
    const member = await findReservationMember(reservation);
    if (!member) return;
    let memberValue = value;
    if (memberField === 'fullName') {
      if (!value) return;
      memberValue = stripAccents(value);
    }
    if (memberField === 'email' && !value) return; // no dejar el member sin email
    await updateDoc(doc(db, 'members', member.id), {
      [memberField]: memberValue === null ? '' : memberValue,
      updatedAt: new Date(),
    });
  };

  const editableField = (key, display) => (
    editField === key ? (
      <TextField
        value={editDraft}
        onChange={(e) => setEditDraft(e.target.value)}
        onBlur={saveEditField}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveEditField();
          if (e.key === 'Escape') setEditField(null);
        }}
        size="small"
        fullWidth
        autoFocus
      />
    ) : (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body1">{display}</Typography>
        <IconButton
          size="small"
          onClick={() => {
            const val = reservation[key];
            setEditDraft(Array.isArray(val) ? val.join(', ') : (val || ''));
            setEditField(key);
          }}
          title="Editar"
        >
          <Iconify icon="eva:edit-fill" width={16} />
        </IconButton>
      </Stack>
    )
  );

  // Requisitos del curso con botón "Guardar" explícito (pedido del cliente
  // 2026-09-02: el staff escribía licencia / Cal. Balas y no tenía forma de
  // saber si se guardó). Los campos son controlados; se sigue guardando
  // también al salir del campo (onBlur) por si no tocan el botón.
  const [courseDraft, setCourseDraft] = useState({ licenseNumber: '', calBalas: '', multas: '' });
  const [courseSaving, setCourseSaving] = useState(false);
  const [courseSavedAt, setCourseSavedAt] = useState(null);
  useEffect(() => {
    if (!reservation) return;
    const cc = reservation.courseChecks || {};
    setCourseDraft({
      licenseNumber: reservation.licenseNumber || '',
      calBalas: typeof cc.calBalas === 'string' ? cc.calBalas.replace(/^\$\s*/, '') : '',
      multas: typeof cc.multas === 'string' ? cc.multas.replace(/^\$\s*/, '') : '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation?.id, reservation?.licenseNumber, reservation?.courseChecks?.calBalas, reservation?.courseChecks?.multas]);

  const courseDraftDirty = reservation && (
    courseDraft.licenseNumber.trim() !== (reservation.licenseNumber || '')
    || courseDraft.calBalas.trim() !== (typeof reservation.courseChecks?.calBalas === 'string' ? reservation.courseChecks.calBalas.replace(/^\$\s*/, '') : '')
    || courseDraft.multas.trim() !== (typeof reservation.courseChecks?.multas === 'string' ? reservation.courseChecks.multas.replace(/^\$\s*/, '') : '')
  );

  const saveCourseRequirements = async () => {
    if (!reservation) return;
    setCourseSaving(true);
    try {
      const lic = courseDraft.licenseNumber.trim();
      const bal = courseDraft.calBalas.trim();
      const mul = courseDraft.multas.trim();
      await updateDoc(doc(db, 'reservations', reservationId), {
        licenseNumber: lic || null,
        'courseChecks.calBalas': bal,
        'courseChecks.multas': mul,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({
        ...prev,
        licenseNumber: lic || null,
        courseChecks: { ...(prev.courseChecks || {}), calBalas: bal, multas: mul },
      }));
      setCourseSavedAt(Date.now());
    } catch (e) {
      console.error(e);
      alert(`Error guardando los requisitos: ${e.message}`);
    }
    setCourseSaving(false);
  };

  const saveLicenseNumber = async (value) => {
    const trimmed = value.trim();
    if ((reservation.licenseNumber || '') === trimmed) return;
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        licenseNumber: trimmed || null,
        updatedAt: new Date(),
      });
      setReservation((prev) => ({ ...prev, licenseNumber: trimmed || null }));
    } catch (e) {
      console.error(e);
      alert('Error guardando el número de licencia.');
    }
  };

  // Borrado permanente de la reservación (con confirmación). Si tiene pago
  // procesado, se avisa que esto NO reembolsa — para eso está Cancel & Refund.
  const handleDeleteReservation = async () => {
    const hasPayment = reservation.paymentStatus === 'succeeded';
    const msg = hasPayment
      ? `Esta reservación tiene un pago procesado de $${((reservation.amountCents || 0) / 100).toFixed(2)}. ` +
        'Eliminarla NO devuelve el dinero (usa "Cancel & Refund" para eso). ¿Eliminar de todas formas?'
      : `¿Eliminar la reservación de "${reservation.userName}"? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    setActionLoading(true);
    try {
      await deleteDoc(doc(db, 'reservations', reservationId));
      if (window.history.state?.idx > 0) navigate(-1);
      else navigate('/dashboard/reservations');
    } catch (e) {
      console.error(e);
      alert('Error eliminando la reservación.');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/adminCancelReservation`, {
        reservationId,
        reason: cancelReason || 'Cancelled by admin',
        issueRefund,
        partnerPenalty: partnerPenaltyOnCancel,
      });
      alert('Reservation cancelled successfully');
      setCancelDialog(false);
      loadReservation();
    } catch (error) {
      alert(`Error cancelling reservation: ${error.response?.data?.error || error.message}`);
    }
    setActionLoading(false);
  };

  // Aliado: poner o quitar a mano la penalidad de una inasistencia o
  // cancelación (queda en partnerPenaltyOverride; el estado de cuenta la usa)
  const handleTogglePartnerPenalty = async () => {
    const next = !partnerPenaltyApplies(reservation);
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'reservations', reservationId), { partnerPenaltyOverride: next, updatedAt: new Date() });
      setReservation((prev) => ({ ...prev, partnerPenaltyOverride: next }));
    } catch (error) {
      alert(`No se pudo guardar: ${error.message}`);
    }
    setActionLoading(false);
  };

  const handleMarkNoShow = async () => {
    if (!window.confirm('Mark this reservation as No Show?')) return;
    setActionLoading(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/adminUpdateReservation`, {
        reservationId,
        status: 'no_show',
      });
      alert('Marked as No Show');
      loadReservation();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    }
    setActionLoading(false);
  };

  const handleMarkCompleted = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/adminUpdateReservation`, {
        reservationId,
        status: 'completed',
      });
      alert('Marked as Completed');
      loadReservation();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || error.message}`);
    }
    setActionLoading(false);
  };

  // "Showed": el cliente llegó. Solo se guarda la bandera (no cambia el
  // status ni el pago). Reemplaza a "Marcar como pagado" — pedido del
  // cliente 2026-09-02. Se escribe directo a Firestore (no requiere deploy).
  // Link de pago: reenviar SMS + email (pedido 2026-10-07)
  const [resendingLink, setResendingLink] = useState(false);
  const handleResendPaymentLink = async () => {
    setResendingLink(true);
    try {
      const resp = await axios.post(`${FUNCTIONS_BASE}/resendPaymentLink`, { reservationId });
      const pl = resp.data || {};
      const sent = [pl.emailSent ? 'email' : null, pl.smsSent ? 'SMS' : null].filter(Boolean).join(' y ');
      alert(sent ? `Link reenviado por ${sent}.` : `No se pudo enviar (${pl.smsError || pl.emailError || 'sin email ni teléfono'}). Copia el link y envíaselo al cliente.`);
      loadReservation();
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Error reenviando el link');
    }
    setResendingLink(false);
  };
  const paymentLinkUrl = reservation?.payToken ? `https://bbtc-dashboard.web.app/pay?t=${reservation.payToken}` : null;
  const copyPaymentLink = async () => {
    if (!paymentLinkUrl) return;
    try { await navigator.clipboard.writeText(paymentLinkUrl); alert('Link copiado.'); } catch (e) { window.prompt('Copia el link:', paymentLinkUrl); }
  };

  // Papel con la cita del curso para el cliente (Yaelis 2026-10-19: "a veces
  // son viejitos y quiere darles un papel con el día y hora"). Se imprime o se
  // guarda como PDF desde el diálogo de impresión del navegador.
  const printAppointmentSlip = () => {
    const r0 = reservation;
    if (!r0) return;
    const w = window.open('', '_blank', 'width=700,height=800');
    if (!w) { alert('El navegador bloqueó la ventana. Permite ventanas emergentes para este sitio.'); return; }
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const dateLabel = r0.date ? new Date(`${r0.date}T12:00:00`).toLocaleDateString('es-PR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const isCourseRes = ['course', 'class'].includes(r0.categoryType);
    const people = [r0.userName, r0.guest && r0.guest.name].filter(Boolean);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Cita ${esc(r0.confirmationCode || '')}</title>
    <style>
      @page{size:letter;margin:0.6in}
      body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;font-size:15px;line-height:1.45}
      h1{font-size:20px;text-align:center;margin:0 0 2px}
      .sub{text-align:center;color:#555;font-size:13px;margin-bottom:18px}
      .box{border:2px solid #222;border-radius:10px;padding:16px 20px;margin:10px 0}
      .big{font-size:26px;font-weight:bold;margin:2px 0}
      .lbl{font-size:12px;color:#666;text-transform:uppercase;letter-spacing:1px;margin-top:10px}
      .code{font-family:monospace;font-size:22px;letter-spacing:3px}
      ul{margin:6px 0 0;padding-left:20px}
      .foot{text-align:center;margin-top:22px;font-size:12px;color:#333;line-height:1.5}
      .ref{margin-top:8px;font-size:10px;color:#777;text-align:right}
    </style></head><body>
    <h1>B&amp;B TARGET CENTER</h1>
    <div class="sub">Confirmación de ${isCourseRes ? 'tu cita al curso' : 'tu reservación'}</div>
    <div class="box">
      <div class="lbl">${isCourseRes ? 'Curso' : 'Reservación'}</div>
      <div class="big">${esc(r0.categoryName || '')}</div>
      <div class="lbl">Fecha</div>
      <div class="big" style="text-transform:capitalize">${esc(dateLabel)}</div>
      <div class="lbl">Hora</div>
      <div class="big">${esc(formatTime12(r0.startTime))}${r0.endTime ? ` – ${esc(formatTime12(r0.endTime))}` : ''}</div>
      ${r0.laneName ? `<div class="lbl">Lane</div><div class="big">${esc(r0.laneName)}</div>` : ''}
      <div class="lbl">A nombre de</div>
      <div>${people.map(esc).join(' y ')}</div>
      ${r0.userPhone ? `<div class="lbl">Teléfono</div><div>${esc(r0.userPhone)}</div>` : ''}
      <div class="lbl">Código de confirmación</div>
      <div class="code">${esc(r0.confirmationCode || '')}</div>
    </div>
    ${isCourseRes ? `<div class="box" style="border-style:dashed">
      <b>Recuerda traer:</b>
      <ul>
        <li>Identificación con foto (licencia de conducir)</li>
        <li>Licencia de armas vigente (si es renovación)</li>
        <li>Los documentos que te indicó el personal</li>
      </ul>
      <div style="margin-top:8px">Llega <b>15 minutos antes</b> de la hora de tu cita.</div>
    </div>` : ''}
    <div class="foot"><b>B &amp; B Target Center, Inc.</b><br/>369 Ave. San Claudio Suite #3, San Juan, PR 00926<br/>Tel. 787-755-9060 / 787-761-0735 · www.bbtargetcenter.com</div>
    <div class="ref">Impreso ${new Date().toLocaleString('es-PR')}</div>
    <script>window.onload=function(){setTimeout(function(){window.print()},150)}</script>
    </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  const handleToggleShowed = async () => {
    const next = !reservation?.showed;
    // Cursos/clases: Showed = Asistió en la página de Cursos (video Lola
    // 2026-10-19), así que también se mueve el status (completed ⇄ confirmed).
    const isCourseRes = ['course', 'class'].includes(reservation?.categoryType);
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        showed: next,
        showedAt: next ? new Date() : null,
        ...(isCourseRes ? { status: next ? 'completed' : 'confirmed' } : {}),
        updatedAt: new Date(),
      });
      loadReservation();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setActionLoading(false);
  };

  // keepSlot = true: abre con la fecha/hora actual ya escogidas (atajo
  // "Cambiar lane": solo se cambia el lane en el mismo horario).
  const openReschedule = (keepSlot = false, duplicate = false) => {
    setDuplicateMode(duplicate);
    setNewCategoryId('');
    setNewDate(reservation?.date || '');
    setNewStartTime(keepSlot ? (reservation?.startTime || '') : '');
    setNewSlots([]);
    setLaneOptions([]);
    setNewLaneId('');
    setLaneError(null);
    setRescheduleError(null);
    setRescheduleOpen(true);
  };

  const closeReschedule = () => {
    setRescheduleOpen(false);
    setDuplicateMode(false);
    setNewCategoryId('');
    setNewSlots([]);
    setNewStartTime('');
    setLaneOptions([]);
    setNewLaneId('');
    setLaneError(null);
    setRescheduleError(null);
  };

  const targetCategoryId = newCategoryId || reservation?.categoryId || '';

  // Categorías de lane activas (para mover pistola ⇄ rifle). Solo en lanes.
  useEffect(() => {
    if (!rescheduleOpen || duplicateMode || reservation?.categoryType !== 'lane') return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'reservation_categories'), where('type', '==', 'lane')));
        setLaneCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false).sort((a, b) => String(a.name).localeCompare(String(b.name))));
      } catch (e) { setLaneCategories([]); }
    })();
  }, [rescheduleOpen, duplicateMode, reservation]);

  // Al abrir el diálogo: listar las próximas fechas en que la categoría abre
  // (según availability_rules) — no un calendario abierto.
  useEffect(() => {
    if (!rescheduleOpen || !reservation) return;
    (async () => {
      setDatesLoading(true);
      try {
        const rulesSnap = await getDocs(query(
          collection(db, 'availability_rules'),
          where('categoryId', '==', targetCategoryId),
        ));
        let rules = rulesSnap.docs.map((d) => d.data()).filter((ru) => ru.active !== false);
        // Lanes también usan reglas genéricas (sin categoryId)
        if (reservation.categoryType === 'lane') {
          const genericSnap = await getDocs(collection(db, 'availability_rules'));
          const generic = genericSnap.docs.map((d) => d.data())
            .filter((ru) => !ru.categoryId && ru.active !== false);
          rules = [...rules, ...generic];
        }
        // Reglas recurrentes (día de la semana) + de fecha fija (una sola vez)
        const allowedDows = new Set(rules.filter((ru) => !ru.date).map((ru) => ru.dayOfWeek));
        const allowedFixedDates = new Set(rules.filter((ru) => ru.date).map((ru) => ru.date));
        const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
        const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const dates = [];
        const today = new Date();
        for (let i = 0; i < 45 && dates.length < 14; i += 1) {
          const d = new Date(today);
          d.setDate(today.getDate() + i);
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          if (allowedDows.has(d.getDay()) || allowedFixedDates.has(iso)) {
            dates.push({ iso, label: `${DIAS[d.getDay()]} ${d.getDate()} ${MESES[d.getMonth()]}` });
          }
        }
        setAvailableDates(dates);
      } catch (e) {
        console.error(e);
        setAvailableDates([]);
      }
      setDatesLoading(false);
    })();
  }, [rescheduleOpen, reservation, targetCategoryId]);

  // Carga slots disponibles cuando cambia la fecha
  useEffect(() => {
    if (!rescheduleOpen || !newDate || !reservation) {
      setNewSlots([]);
      return undefined;
    }
    let cancelled = false;
    setNewSlotsLoading(true);
    setNewSlots([]);
    (async () => {
      try {
        const r = await axios.post(`${FUNCTIONS_BASE}/getAvailableSlots`, {
          categoryId: targetCategoryId,
          date: newDate,
          includePast: true, // admin puede reagendar a horas actuales/pasadas del día
        });
        if (!cancelled && r.data && r.data.slots) {
          // El slot actual también se lista: se puede quedar en la misma hora
          // y cambiar solo el lane.
          setNewSlots(r.data.slots || []);
        }
      } catch (e) {
        if (!cancelled) setNewSlots([]);
      }
      if (!cancelled) setNewSlotsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [rescheduleOpen, newDate, reservation, targetCategoryId]);

  // Lanes disponibles en la fecha/hora escogida (solo categorías de lane).
  // getAvailableLanes excluye esta misma reservación para que su lane actual
  // no salga "ocupado" por sí misma.
  useEffect(() => {
    if (!rescheduleOpen || !newDate || !newStartTime || !reservation || reservation.categoryType !== 'lane') {
      setLaneOptions([]);
      setNewLaneId('');
      return undefined;
    }
    let cancelled = false;
    setLanesLoading(true);
    setLaneError(null);
    (async () => {
      try {
        const resp = await axios.post(`${FUNCTIONS_BASE}/getAvailableLanes`, {
          categoryId: targetCategoryId,
          date: newDate,
          startTime: newStartTime,
          // Al duplicar, la reservación original sigue ocupando su lane:
          // no se excluye.
          ...(duplicateMode ? {} : { excludeReservationId: reservationId }),
        });
        if (cancelled) return;
        const lanes = resp.data?.lanes || [];
        setLaneOptions(lanes);
        // Preseleccionar el lane actual si sigue libre en el nuevo horario
        const current = lanes.find((l) => l.id === reservation.laneId);
        if (current && current.available !== false) {
          setNewLaneId(current.id);
        } else if (duplicateMode) {
          // Duplicar: si el lane actual está ocupado, otro libre automáticamente
          const free = lanes.find((l) => l.available !== false);
          setNewLaneId(free ? free.id : '');
        } else {
          setNewLaneId('');
        }
      } catch (e) {
        if (cancelled) return;
        setLaneOptions([]);
        setNewLaneId('');
        setLaneError(e.response?.data?.error || e.message);
      }
      if (!cancelled) setLanesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [rescheduleOpen, newDate, newStartTime, reservation, reservationId, duplicateMode, targetCategoryId]);

  // Duplicar: crea una reservación nueva (adminCreateReservation) con los
  // datos de esta en la fecha/hora/lane escogidos y abre la nueva.
  const handleDuplicate = async () => {
    const isLane = reservation?.categoryType === 'lane';
    if (!newDate || !newStartTime) {
      setRescheduleError('Selecciona fecha y hora.');
      return;
    }
    if (isLane && laneOptions.length > 0 && !laneOptions.some((l) => l.available !== false)) {
      setRescheduleError('No hay lanes disponibles para ese horario.');
      return;
    }
    setActionLoading(true);
    setRescheduleError(null);
    try {
      const r0 = reservation;
      const resp = await axios.post(`${FUNCTIONS_BASE}/adminCreateReservation`, {
        categoryId: r0.categoryId,
        date: newDate,
        startTime: newStartTime,
        // Sin laneId el backend escoge uno libre por su cuenta.
        ...(isLane && newLaneId ? { laneId: newLaneId } : {}),
        customerEmail: r0.userEmail || null,
        customerPhone: r0.userPhone || null,
        customerName: r0.userName || null,
        licenseNumber: r0.licenseNumber || null,
        calibers: Array.isArray(r0.calibers) ? r0.calibers : [],
        notes: r0.notes || '',
        courseLanguage: r0.courseLanguage || null,
        licenseType: r0.licenseType || null,
        createdBy: 'admin',
      });
      const newId = resp.data?.reservationId;
      closeReschedule();
      alert(`Reservación duplicada${resp.data?.laneName ? ` en ${resp.data.laneName}` : ''}`);
      if (newId) navigate(`/dashboard/reservations/details?id=${newId}`);
    } catch (error) {
      setRescheduleError(error.response?.data?.error || error.message || 'Error duplicando');
    }
    setActionLoading(false);
  };

  const handleReschedule = async () => {
    const isLane = reservation?.categoryType === 'lane';
    if (!newDate || !newStartTime) {
      setRescheduleError('Selecciona fecha y hora.');
      return;
    }
    if (isLane && !newLaneId) {
      setRescheduleError('Selecciona un lane disponible para ese horario.');
      return;
    }
    const categoryChanged = isLane && newCategoryId && newCategoryId !== reservation.categoryId;
    const nothingChanged = !categoryChanged
      && newDate === reservation.date
      && newStartTime === reservation.startTime
      && (!isLane || newLaneId === reservation.laneId);
    if (nothingChanged) {
      setRescheduleError('No cambiaste nada: escoge otra fecha, hora o lane.');
      return;
    }
    setActionLoading(true);
    setRescheduleError(null);
    try {
      await axios.post(`${FUNCTIONS_BASE}/adminUpdateReservation`, {
        reservationId,
        date: newDate,
        startTime: newStartTime,
        ...(isLane && newLaneId ? { laneId: newLaneId } : {}),
        ...(categoryChanged ? { categoryId: newCategoryId } : {}),
      });
      closeReschedule();
      loadReservation();
      alert('Reservación actualizada correctamente');
    } catch (error) {
      setRescheduleError(error.response?.data?.error || error.message || 'Error reagendando');
    }
    setActionLoading(false);
  };

  const formatTime12 = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  // ---- Acompañante: editar / quitar ----
  const openGuestDialog = () => {
    const g = reservation?.guest || {};
    setGuestForm({
      name: g.name || '',
      phone: g.phone || '',
      email: g.email || '',
      licenseNumber: g.licenseNumber || '',
    });
    setGuestError(null);
    setGuestDialogOpen(true);
  };

  const handleSaveGuest = async () => {
    if (!guestForm.name.trim()) {
      setGuestError('El nombre es requerido.');
      return;
    }
    setActionLoading(true);
    setGuestError(null);
    try {
      await axios.post(`${FUNCTIONS_BASE}/adminUpdateGuest`, {
        reservationId,
        guest: {
          name: guestForm.name.trim(),
          phone: guestForm.phone.replace(/\D/g, ''),
          email: guestForm.email.trim().toLowerCase() || null,
          licenseNumber: guestForm.licenseNumber.trim() || null,
        },
      });
      setGuestDialogOpen(false);
      loadReservation();
    } catch (error) {
      setGuestError(error.response?.data?.error || error.message || 'Error guardando el acompañante');
    }
    setActionLoading(false);
  };

  const handleRemoveGuest = async () => {
    const hasOwnDoc = reservation?.categoryType !== 'lane' || Boolean(reservation?.guest?.laneId);
    const msg = hasOwnDoc
      ? '¿Quitar al acompañante de esta reservación? Su cupo/lane quedará libre (su registro se cancela).'
      : '¿Quitar al acompañante de esta reservación?';
    if (!window.confirm(msg)) return;
    setActionLoading(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/adminUpdateGuest`, { reservationId, guest: null });
      setGuestDialogOpen(false);
      loadReservation();
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Error quitando el acompañante');
    }
    setActionLoading(false);
  };

  if (loading) return (
    <Container>
      <Stack direction="row" alignItems="center" mb={3}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1 }} />
        <Skeleton variant="text" width={200} height={36} />
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 1 }} />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {Array.from({ length: 6 }, (_, i) => (
                <Grid item xs={6} key={i}>
                  <Skeleton variant="text" width={80} height={16} />
                  <Skeleton variant="text" width={140} height={24} />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Skeleton variant="text" width={180} height={24} sx={{ mb: 1 }} />
            <Grid container spacing={2}>
              {Array.from({ length: 6 }, (_, i) => (
                <Grid item xs={6} key={i}>
                  <Skeleton variant="text" width={80} height={16} />
                  <Skeleton variant="text" width={130} height={24} />
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width={100} height={24} sx={{ mb: 1 }} />
            {Array.from({ length: 3 }, (_, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={60} />
              </Stack>
            ))}
          </Card>
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width={80} height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={42} sx={{ borderRadius: 1.5, mb: 1 }} />
            <Skeleton variant="rounded" height={42} sx={{ borderRadius: 1.5 }} />
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
  if (!reservation) return <Container><Typography>Reservation not found</Typography></Container>;

  const r = reservation;
  // ¿Curso privado? Por el switch de la categoría o por el nombre
  const rIsPrivate = isPrivateCourse(reservationCategory || { name: r.categoryName, type: r.categoryType });
  const canCancel = ['confirmed', 'pending_payment'].includes(r.status);
  const canMarkNoShow = r.status === 'confirmed';
  const canMarkCompleted = r.status === 'confirmed';
  const canReschedule = ['confirmed', 'pending_payment'].includes(r.status);
  // Solo aplica a reservaciones de lane; el lane es físico, así que solo se
  // ofrecen lanes libres en ese horario.
  const canChangeLane = r.categoryType === 'lane' && ['confirmed', 'pending_payment'].includes(r.status);
  const canMarkShowed = ['confirmed', 'completed'].includes(r.status);

  // Aliado (armería): qué se le factura por esta inscripción y si cancelarla
  // ahora sería tardía (menos de la ventana del curso, 24 h por defecto)
  const partnerBill = r.partnerId ? partnerBillingOf(r) : null;
  const partnerRental = r.partnerId ? partnerRentalReason(r) : null;
  const partnerWindowHours = (() => {
    const raw = reservationCategory ? reservationCategory.rescheduleHoursBefore : null;
    const h = Number(raw);
    return raw !== null && raw !== undefined && raw !== '' && Number.isFinite(h) ? h : PARTNER_CHANGE_HOURS;
  })();
  const partnerCancelIsLate = Boolean(r.partnerId)
    && (new Date(`${r.date}T${r.startTime}:00`).getTime() - Date.now()) / 36e5 < partnerWindowHours;
  let partnerBillNote = '';
  if (partnerBill) {
    if (partnerBill.kind === 'course') partnerBillNote = `Asistió: ${money(partnerBill.priceCents)} − ${money(partnerBill.commissionCents)} de comisión`;
    else if (partnerBill.kind === 'no_show') partnerBillNote = 'Penalidad por inasistencia (sin comisión)';
    else if (partnerBill.kind === 'late_cancel') partnerBillNote = `Penalidad por cancelar con menos de ${partnerWindowHours} h (sin comisión)`;
    else if (partnerBill.kind === 'upcoming') partnerBillNote = `Si asiste: ${money((r.partnerPriceCents || 0) - (r.partnerCommissionCents || 0))}. Si no se presenta: penalidad de ${money(partnerPenaltyCentsOf(r))}.`;
    else if (r.status === 'no_show' || r.partnerLateCancel === true) partnerBillNote = 'Penalidad quitada a mano';
    else if (r.status === 'cancelled') partnerBillNote = 'Cancelada a tiempo: no se factura';
  }

  return (
    <>
      <Helmet>
        <title>Reservation Details | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" mb={3}>
          {/* Volver a la página anterior (Cursos o Reservations); fallback si se abrió directo */}
          <IconButton
            onClick={() => {
              if (window.history.state?.idx > 0) navigate(-1);
              else navigate('/dashboard/reservations');
            }}
            sx={{ mr: 1 }}
          >
            <Iconify icon="eva:arrow-back-fill" />
          </IconButton>
          <Typography variant="h4">Reservation Details</Typography>
        </Stack>

        <Grid container spacing={3}>
          {/* Main Info */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="h5">{r.categoryName}</Typography>
                  {rIsPrivate && r.courseLanguage && (
                    <Chip size="small" color="error" variant="outlined" label={r.courseLanguage === 'en' ? 'Inglés' : 'Español'} />
                  )}
                  {rIsPrivate && r.licenseType && (
                    <Chip size="small" color="error" variant="outlined" label={r.licenseType === 'renewal' ? 'Renovación' : 'Licencia nueva'} />
                  )}
                  {r.partnerName && (
                    <Chip size="small" color="warning" label={`Aliado: ${r.partnerName} — curso cobrado por la armería`} />
                  )}
                  {r.partnerName && partnerRental === 'expired' && (
                    <Chip size="small" color="error" label="Licencia vencida: renta arma y compra ammo aquí" />
                  )}
                  {r.partnerName && partnerRental === 'new' && (
                    <Chip size="small" color="warning" variant="outlined" label="Licencia nueva: renta de arma y ammo se cobran aquí" />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {r.showed && <Chip label="Showed" color="success" size="small" icon={<Iconify icon="eva:person-done-fill" />} />}
                  <Chip label={r.status.replace('_', ' ')} color={statusColors[r.status] || 'default'} />
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1" gutterBottom>Customer Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  {editableField('userName', r.userName)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  {editableField('userEmail', r.userEmail || 'N/A')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  {editableField('userPhone', r.userPhone || 'N/A')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Member</Typography>
                  <Chip label={r.isMember ? 'Yes' : 'No'} color={r.isMember ? 'success' : 'default'} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">License Number</Typography>
                  {editableField('licenseNumber', r.licenseNumber || 'N/A')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Member ID</Typography>
                  {editableField('memberId', r.memberId || membership?.member?.memberId || 'N/A')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Calibres (registro del range)</Typography>
                  {editableField('calibers', (r.calibers || []).length ? r.calibers.join(', ') : 'N/A')}
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>Reservación</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography variant="body1">{r.date}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Time</Typography>
                  <Typography variant="body1">{formatTime(r.startTime)} - {formatTime(r.endTime)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography variant="body1">{r.durationMinutes} minutes</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Lane</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body1">{r.laneName || 'N/A'}</Typography>
                    {canChangeLane && (
                      <IconButton size="small" onClick={() => openReschedule(true)} title="Cambiar lane">
                        <Iconify icon="eva:edit-fill" width={16} />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Confirmation Code</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>{r.confirmationCode}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Categoría</Typography>
                  {/* El cliente pidió ver el nombre de la categoría (p.ej. "Lane Rifle")
                      y no solo el tipo genérico "lane" — así sabe qué tipo de lane es. */}
                  <Typography variant="body1">{r.categoryName || CATEGORY_TYPE_LABELS[r.categoryType] || r.categoryType || 'N/A'}</Typography>
                  {r.categoryName && r.categoryType && (
                    <Typography variant="caption" color="text.secondary">
                      Tipo: {CATEGORY_TYPE_LABELS[r.categoryType] || r.categoryType}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {/* Acompañante: en lanes comparte el mismo lane, así que no tiene
                  su propia reservación — sin esto el empleado no sabría que
                  llegan dos personas. */}
              {r.guest && r.guest.name && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1">Acompañante</Typography>
                    <Chip
                      label={(() => {
                        if (r.categoryType !== 'lane') return 'Ocupa su propio cupo';
                        return r.guest.laneName
                          ? `Lane propio: ${r.guest.laneName}`
                          : 'Comparte el mismo lane';
                      })()}
                      size="small"
                      color="warning"
                    />
                    {['confirmed', 'pending_payment'].includes(r.status) && (
                      <>
                        <IconButton size="small" onClick={openGuestDialog} title="Editar acompañante" disabled={actionLoading}>
                          <Iconify icon="eva:edit-fill" width={16} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={handleRemoveGuest} title="Quitar acompañante" disabled={actionLoading}>
                          <Iconify icon="eva:trash-2-outline" width={16} />
                        </IconButton>
                      </>
                    )}
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Nombre</Typography>
                      <Typography variant="body1">{r.guest.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                      <Typography variant="body1">{r.guest.phone || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Licencia</Typography>
                      <Typography variant="body1">{r.guest.licenseNumber || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Miembro</Typography>
                      <Chip label={r.guest.isMember ? 'Yes' : 'No'} color={r.guest.isMember ? 'success' : 'default'} size="small" />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Reservación hecha a nombre de otra persona */}
              {r.reservationFor && r.reservationFor.name && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>Reservado para otra persona</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Nombre</Typography>
                      <Typography variant="body1">{r.reservationFor.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                      <Typography variant="body1">{r.reservationFor.phone || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Licencia</Typography>
                      <Typography variant="body1">{r.reservationFor.licenseNumber || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Miembro</Typography>
                      <Chip label={r.reservationFor.isMember ? 'Yes' : 'No'} color={r.reservationFor.isMember ? 'success' : 'default'} size="small" />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Personas adicionales — registro del campo de tiro. El staff
                  añade aquí a cualquiera que tiró bajo esta reservación y
                  esas personas salen en el reporte del registro del range. */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Personas adicionales (registro del range)</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Personas que estuvieron en el range bajo esta reservación. Salen en el reporte del registro del campo de tiro.
              </Typography>

              {(r.attendees || []).map((a, idx) => (
                <Stack
                  key={`${a.name}-${idx}`}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ py: 1, px: 1.5, mb: 1, bgcolor: '#F8F9FA', borderRadius: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{a.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[a.phone, a.licenseNumber ? `Lic: ${a.licenseNumber}` : null, (a.calibers || []).length ? `Cal: ${a.calibers.join(', ')}` : null]
                        .filter(Boolean).join(' • ') || 'Sin datos adicionales'}
                    </Typography>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => handleRemoveAttendee(idx)} title="Quitar">
                    <Iconify icon="eva:trash-2-outline" width={18} />
                  </IconButton>
                </Stack>
              ))}

              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Teléfono"
                    value={attendeeForm.phone}
                    onChange={(e) => { setAttendeeForm((p) => ({ ...p, phone: e.target.value })); setAttendeeLookup(null); }}
                    onBlur={lookupAttendeeByPhone}
                    size="small"
                    fullWidth
                    placeholder="787-555-1234"
                    helperText={
                      attendeeLookup === 'searching' ? 'Buscando...' :
                      attendeeLookup === 'found' ? 'Encontrado en sistema' :
                      attendeeLookup === 'not_found' ? 'No está en sistema' : ' '
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Nombre"
                    value={attendeeForm.name}
                    onChange={(e) => setAttendeeForm((p) => ({ ...p, name: e.target.value }))}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    label="Licencia"
                    value={attendeeForm.licenseNumber}
                    onChange={(e) => setAttendeeForm((p) => ({ ...p, licenseNumber: e.target.value }))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    label="Calibres"
                    value={attendeeForm.calibers}
                    onChange={(e) => setAttendeeForm((p) => ({ ...p, calibers: e.target.value }))}
                    size="small"
                    fullWidth
                    placeholder="9MM, 45 ACP"
                    required={reservationAsksCaliber}
                    helperText={reservationAsksCaliber ? 'Requerido' : ' '}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Button
                    variant="contained"
                    onClick={handleAddAttendee}
                    disabled={!attendeeForm.name.trim() || (reservationAsksCaliber && !attendeeForm.calibers.trim()) || attendeeSaving}
                    sx={{ minWidth: 0, px: 2 }}
                  >
                    {attendeeSaving ? <CircularProgress size={18} color="inherit" /> : 'Añadir'}
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Payment + Actions */}
          <Grid item xs={12} md={4}>
            {partnerBill && (
              <Card sx={{ p: 3, mb: 3, bgcolor: '#FFF8E1' }}>
                <Typography variant="subtitle1" gutterBottom>Armería (aliado)</Typography>
                <Typography variant="body2" fontWeight={600}>{r.partnerName}{r.partnerCode ? ` · ${r.partnerCode}` : ''}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Precio {money(r.partnerPriceCents)} · comisión {money(r.partnerCommissionCents)} · penalidad {money(partnerPenaltyCentsOf(r))}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Se le factura</Typography>
                  <Typography variant="h6" color={partnerBill.kind === 'no_show' || partnerBill.kind === 'late_cancel' ? 'error.main' : 'text.primary'}>
                    {partnerBill.kind === 'upcoming' ? 'Pendiente' : money(partnerBill.billedCents)}
                  </Typography>
                </Stack>
                {partnerBillNote && <Typography variant="caption" color="text.secondary">{partnerBillNote}</Typography>}
                {(r.status === 'no_show' || r.status === 'cancelled') && (
                  <FormControlLabel
                    sx={{ mt: 1, display: 'flex' }}
                    control={<Switch checked={partnerPenaltyApplies(r)} onChange={handleTogglePartnerPenalty} disabled={actionLoading} />}
                    label={<Typography variant="body2">Cobrar penalidad de {money(partnerPenaltyCentsOf(r))}</Typography>}
                  />
                )}
              </Card>
            )}
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Payment</Typography>
              <Stack spacing={1}>
                {Number(r.taxCents) > 0 && (
                  <>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                      <Typography variant="body2">${((r.subtotalCents || 0) / 100).toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">IVU 11.5%</Typography>
                      <Typography variant="body2">${((r.taxCents || 0) / 100).toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                      <Typography variant="body2" fontWeight={600}>${((r.totalCents || 0) / 100).toFixed(2)}</Typography>
                    </Stack>
                  </>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{Number(r.taxCents) > 0 && r.totalCents > r.amountCents ? 'Cobrado (depósito)' : 'Amount'}</Typography>
                  <Typography variant="h6">${((r.amountCents || 0) / 100).toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                  <Chip label={r.paymentStatus} size="small" color={r.paymentStatus === 'succeeded' ? 'success' : r.paymentStatus === 'refunded' ? 'secondary' : 'default'} />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Price Type</Typography>
                  <Typography variant="body2">{r.isMember ? 'Member' : 'Non-Member'}</Typography>
                </Stack>
                {r.stripePaymentIntentId && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Stripe ID</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.stripePaymentIntentId}</Typography>
                  </Stack>
                )}
              </Stack>
              {/* Link de pago (SMS/email) — pedido 2026-10-07 */}
              {(r.payToken || (r.status === 'pending_payment' && r.createdByAdmin)) && (
                <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: r.status === 'pending_payment' ? '#FFF8E1' : '#E8F5E9' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Link de pago {r.status === 'pending_payment' ? '· pendiente' : r.paidViaLinkAt ? '· pagado por el link' : ''}
                  </Typography>
                  {r.paymentLinkSentAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Último envío: {fmtDate(r.paymentLinkSentAt)}{Number(r.paymentLinkSentCount) > 1 ? ` (${r.paymentLinkSentCount} envíos)` : ''}
                      {r.paymentLinkLastResult ? ` — email ${r.paymentLinkLastResult.emailSent ? '✓' : '✗'}, SMS ${r.paymentLinkLastResult.smsSent ? '✓' : '✗'}` : ''}
                    </Typography>
                  )}
                  {r.paymentLinkLastResult && (r.paymentLinkLastResult.smsError || r.paymentLinkLastResult.emailError) && (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                      {r.paymentLinkLastResult.smsError ? `SMS: ${r.paymentLinkLastResult.smsError}` : ''}{r.paymentLinkLastResult.smsError && r.paymentLinkLastResult.emailError ? ' · ' : ''}{r.paymentLinkLastResult.emailError ? `Email: ${r.paymentLinkLastResult.emailError}` : ''}
                    </Typography>
                  )}
                  {r.status === 'pending_payment' && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="contained" color="warning" onClick={handleResendPaymentLink} disabled={resendingLink || actionLoading} startIcon={resendingLink ? <CircularProgress size={14} /> : <Iconify icon="eva:paper-plane-fill" />}>
                        {resendingLink ? 'Enviando…' : 'Reenviar link (SMS + email)'}
                      </Button>
                      {paymentLinkUrl && (
                        <Button size="small" variant="outlined" onClick={copyPaymentLink} startIcon={<Iconify icon="eva:copy-outline" />}>Copiar link</Button>
                      )}
                    </Stack>
                  )}
                </Box>
              )}
              {r.isMember && Number(r.amountCents || 0) === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Sin cargo por ser miembro{membership?.purchases?.length ? ' — el pago de la membresía está abajo' : ''}.
                </Typography>
              )}
            </Card>

            {membership && (r.isMember || membership.member.active || membership.purchases.length > 0) && (() => {
              const m = membership.member;
              const last = membership.purchases[0];
              const paidAt = last ? toDate(last.decidedAt || last.createdAt) : null;
              const resCreated = toDate(r.createdAt);
              // Compró la membresía y enseguida reservó (mismo día, antes de reservar)
              const boughtWithReservation = paidAt && resCreated
                && paidAt <= new Date(resCreated.getTime() + 5 * 60000)
                && resCreated - paidAt < 24 * 3600000;
              const exp = toDate(m.expiration);
              const expired = exp && exp < new Date();
              return (
                <Card sx={{ p: 3, mb: 3, bgcolor: boughtWithReservation ? '#E8F5E9' : undefined }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">Membresía</Typography>
                    <Chip
                      size="small"
                      label={m.active && !expired ? 'Activa' : 'Inactiva'}
                      color={m.active && !expired ? 'success' : 'default'}
                    />
                  </Stack>
                  {boughtWithReservation && (
                    <Alert severity="success" sx={{ mb: 1.5, py: 0 }}>
                      Compró la membresía al hacer esta reservación
                    </Alert>
                  )}
                  <Stack spacing={1}>
                    {last ? (
                      <>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Plan</Typography>
                          <Typography variant="body2" fontWeight={600} textAlign="right">
                            {last.planName || '—'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Tipo</Typography>
                          <Typography variant="body2">
                            {last.autoRenewal ? 'Renovación automática' : last.isRenewal ? 'Renovación' : 'Nueva'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Precio</Typography>
                          <Typography variant="body2">{money(last.priceCents)}</Typography>
                        </Stack>
                        {Number(last.taxCents) > 0 && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">IVU 11.5%</Typography>
                            <Typography variant="body2">{money(last.taxCents)}</Typography>
                          </Stack>
                        )}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Pagó</Typography>
                          <Typography variant="h6">{money(last.totalCents || (Number(last.priceCents || 0) + Number(last.taxCents || 0)))}</Typography>
                        </Stack>
                        {last.paymentMethodLabel && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Tarjeta</Typography>
                            <Typography variant="body2">{last.paymentMethodLabel}</Typography>
                          </Stack>
                        )}
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Fecha de pago</Typography>
                          <Typography variant="body2">{fmtDateTime(paidAt)}</Typography>
                        </Stack>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay pago de membresía registrado en el sistema (pudo cobrarse en el local o activarse a mano).
                      </Typography>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Vence</Typography>
                      <Typography variant="body2" color={expired ? 'error.main' : 'text.primary'}>{fmtDate(exp)}</Typography>
                    </Stack>
                    {m.memberId && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Member #</Typography>
                        <Typography variant="body2">{m.memberId}</Typography>
                      </Stack>
                    )}
                    {last?.paymentIntentId && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Stripe ID</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{last.paymentIntentId}</Typography>
                      </Stack>
                    )}
                    {membership.purchases.length > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        Pagos anteriores: {membership.purchases.slice(1, 4).map((p) => `${money(p.totalCents)} (${fmtDate(p.decidedAt || p.createdAt)})`).join(' · ')}
                      </Typography>
                    )}
                  </Stack>
                  <Button
                    size="small"
                    sx={{ mt: 1.5 }}
                    onClick={() => navigate('/dashboard/membership-requests')}
                    endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                  >
                    Ver solicitudes de membresía
                  </Button>
                </Card>
              );
            })()}

            {r.cancelledAt && (
              <Card sx={{ p: 3, mb: 3, bgcolor: '#fff3e0' }}>
                <Typography variant="subtitle1" gutterBottom>Cancellation</Typography>
                <Typography variant="body2">By: {r.cancelledBy === 'partner' ? `armería (${r.partnerName || 'aliado'})` : r.cancelledBy}</Typography>
                <Typography variant="body2">Reason: {r.cancellationReason}</Typography>
                <Typography variant="caption" color="text.secondary">{new Date(r.cancelledAt).toLocaleString()}</Typography>
              </Card>
            )}

            <Card sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Actions</Typography>
              <Stack spacing={1.5}>
                {canMarkShowed && (
                  <Button
                    fullWidth
                    variant={r.showed ? 'outlined' : 'contained'}
                    color="success"
                    onClick={handleToggleShowed}
                    disabled={actionLoading}
                    startIcon={<Iconify icon={r.showed ? 'eva:checkmark-circle-2-fill' : 'eva:person-done-outline'} />}
                  >
                    {r.showed ? 'Showed ✓ (quitar)' : 'Showed'}
                  </Button>
                )}
                {canReschedule && (
                  <Button fullWidth variant="outlined" color="primary" onClick={() => openReschedule(false)} disabled={actionLoading} startIcon={<Iconify icon="eva:calendar-fill" />}>
                    {r.categoryType === 'lane' ? 'Cambiar fecha / hora / lane' : 'Cambiar fecha/hora'}
                  </Button>
                )}
                {canChangeLane && (
                  <Button fullWidth variant="outlined" color="primary" onClick={() => openReschedule(true)} disabled={actionLoading} startIcon={<Iconify icon="eva:edit-fill" />}>
                    Cambiar solo el lane
                  </Button>
                )}
                {!r.isBlock && (
                  <Button fullWidth variant="outlined" color="info" onClick={() => openReschedule(false, true)} disabled={actionLoading} startIcon={<Iconify icon="eva:copy-fill" />} title="Crea otra reservación igual (mismo cliente) en otra fecha/hora">
                    Duplicar reservación
                  </Button>
                )}
                {!r.isBlock && !['cancelled', 'refunded', 'expired'].includes(r.status) && (
                  <Button fullWidth variant="outlined" onClick={printAppointmentSlip} disabled={actionLoading} startIcon={<Iconify icon="eva:printer-outline" />} title="Papel con el día, hora y código de la cita para entregarle al cliente (se puede guardar como PDF)">
                    Imprimir cita (PDF)
                  </Button>
                )}
                {canCancel && (
                  <Button fullWidth variant="contained" color="error" onClick={() => { setPartnerPenaltyOnCancel(true); setCancelDialog(true); }} disabled={actionLoading}>
                    Cancel Reservation
                  </Button>
                )}
                {canMarkNoShow && (
                  <Button fullWidth variant="outlined" color="warning" onClick={handleMarkNoShow} disabled={actionLoading}>
                    Mark as No Show
                  </Button>
                )}
                {canMarkCompleted && (
                  <Button fullWidth variant="outlined" color="success" onClick={handleMarkCompleted} disabled={actionLoading} title="Libera el lane/cupo (por ejemplo si el cliente se va antes)">
                    Mark as Completed
                  </Button>
                )}
                {!canCancel && !canMarkNoShow && !canMarkCompleted && !canMarkShowed && (
                  <Typography variant="body2" color="text.secondary">No actions available</Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<Iconify icon="eva:trash-2-outline" />}
                  onClick={handleDeleteReservation}
                  disabled={actionLoading}
                >
                  Eliminar reservación
                </Button>
              </Stack>
            </Card>

            {/* Notas internas — p.ej. "Pagado $20 en el sistema viejo", para
                migrar clientes sin tener que consultar el sistema anterior. */}
            <Card sx={{ p: 3, mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Notas internas</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Solo las ven los empleados. No se le envían al cliente.
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Ej: Pagado $20 en el sistema viejo."
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip
                  label="+ Pagado en sistema viejo"
                  size="small"
                  variant="outlined"
                  onClick={() => setNotesDraft((prev) => (
                    prev.trim() ? `${prev.trim()}\nPagado $ en el sistema viejo.` : 'Pagado $ en el sistema viejo.'
                  ))}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveNotes}
                  disabled={notesSaving || notesDraft === (r.notes || '')}
                >
                  {notesSaving ? 'Guardando...' : 'Guardar nota'}
                </Button>
                {notesSaved && <Typography variant="caption" color="success.main">Guardado</Typography>}
              </Stack>
            </Card>

            {(r.categoryType === 'course' || r.categoryType === 'class') && (
              <Card sx={{ p: 3, mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Requisitos del curso</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Lo que marques aquí sale con ✕ en la hoja de asistencia impresa.
                </Typography>
                {/* Idioma y nueva/renovación — SOLO en cursos privados (define
                    el pago al instructor). Tocar la opción marcada la quita. */}
                {rIsPrivate && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Idioma del curso (privado)</Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                      {COURSE_LANGUAGE_OPTIONS.map((o) => (
                        <Chip
                          key={o.value}
                          label={o.label}
                          clickable
                          onClick={() => savePrivateCourseField('courseLanguage', o.value)}
                          onDelete={r.courseLanguage === o.value ? () => savePrivateCourseField('courseLanguage', o.value) : undefined}
                          color={r.courseLanguage === o.value ? 'error' : 'default'}
                          variant={r.courseLanguage === o.value ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Stack>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Tipo de licencia (privado)</Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      {LICENSE_TYPE_OPTIONS.map((o) => (
                        <Chip
                          key={o.value}
                          label={o.label}
                          clickable
                          onClick={() => savePrivateCourseField('licenseType', o.value)}
                          onDelete={r.licenseType === o.value ? () => savePrivateCourseField('licenseType', o.value) : undefined}
                          color={r.licenseType === o.value ? 'error' : 'default'}
                          variant={r.licenseType === o.value ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      {privateCourseSummary(r, reservationCategory || undefined)
                        ? 'Toca la opción marcada (o su ✕) para quitarla.'
                        : 'Sin idioma ni tipo de licencia — en cursos privados esto define el pago al instructor.'}
                    </Typography>
                  </>
                )}
                {!rIsPrivate && (r.courseLanguage || r.licenseType) && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={(
                      <Button color="inherit" size="small" onClick={clearPrivateCourseFields}>Quitar</Button>
                    )}
                  >
                    Esta reservación tiene idioma/tipo de licencia de curso privado, pero este curso no es privado.
                  </Alert>
                )}
                <TextField
                  label="# Licencia"
                  size="small"
                  fullWidth
                  value={courseDraft.licenseNumber}
                  onChange={(e) => setCourseDraft((p) => ({ ...p, licenseNumber: e.target.value }))}
                  onBlur={(e) => saveLicenseNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveCourseRequirements()}
                  sx={{ mb: 2 }}
                />
                <FormGroup>
                  {COURSE_CHECKS.map((c) => (
                    c.input ? (
                      <TextField
                        key={c.key}
                        label={c.label}
                        placeholder={c.placeholder}
                        size="small"
                        value={courseDraft[c.key] ?? ''}
                        onChange={(e) => setCourseDraft((p) => ({ ...p, [c.key]: e.target.value.replace(/^\$\s*/, '') }))}
                        onBlur={(e) => saveCourseCheckText(c.key, e.target.value.replace(/^\$\s*/, ''))}
                        onKeyDown={(e) => e.key === 'Enter' && saveCourseRequirements()}
                        helperText="Lo escrito sale impreso en la celda"
                        InputProps={c.money ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined}
                        sx={{ my: 0.75 }}
                      />
                    ) : (
                      <FormControlLabel
                        key={c.key}
                        control={
                          <Checkbox
                            checked={Boolean(r.courseChecks && r.courseChecks[c.key])}
                            onChange={(e) => toggleCourseCheck(c.key, e.target.checked)}
                            size="small"
                          />
                        }
                        label={c.label}
                      />
                    )
                  ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(r.courseChecks && r.courseChecks.soloCurso)}
                        onChange={(e) => toggleCourseCheck('soloCurso', e.target.checked)}
                        size="small"
                      />
                    }
                    label={'Solo Curso — imprime "Solo Curso" en la línea en vez de los checks'}
                  />
                </FormGroup>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={saveCourseRequirements}
                    disabled={courseSaving}
                    startIcon={<Iconify icon="eva:save-outline" />}
                  >
                    {courseSaving ? 'Guardando...' : 'Guardar licencia y balas'}
                  </Button>
                  {courseSavedAt && !courseDraftDirty && (
                    <Typography variant="caption" color="success.main">Guardado ✓</Typography>
                  )}
                  {courseDraftDirty && !courseSaving && (
                    <Typography variant="caption" color="warning.main">Cambios sin guardar</Typography>
                  )}
                </Stack>
              </Card>
            )}

            <Card sx={{ p: 2, mt: 3 }}>
              <Typography variant="caption" color="text.secondary">Created: {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</Typography>
              <br />
              <Typography variant="caption" color="text.secondary">Updated: {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}</Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Reservation</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} sx={{ mt: 2, mb: 2 }} />
          {r.paymentStatus === 'succeeded' && (
            <FormControlLabel
              control={<Switch checked={issueRefund} onChange={(e) => setIssueRefund(e.target.checked)} />}
              label="Issue full refund"
            />
          )}
          {partnerCancelIsLate && (
            <Box sx={{ p: 2, bgcolor: '#FFF8E1', borderRadius: 1 }}>
              <FormControlLabel
                control={<Switch checked={partnerPenaltyOnCancel} onChange={(e) => setPartnerPenaltyOnCancel(e.target.checked)} />}
                label={`Cobrarle a ${r.partnerName || 'la armería'} la penalidad de ${money(partnerPenaltyCentsOf(r))} (faltan menos de ${partnerWindowHours} h)`}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Apágalo si la cancelación es por parte de BBTC. Después se puede cambiar en «Armería (aliado)».
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Back</Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={actionLoading}>
            {actionLoading ? 'Processing...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onClose={closeReschedule} maxWidth="sm" fullWidth>
        <DialogTitle>
          {duplicateMode
            ? 'Duplicar reservación'
            : (reservation?.categoryType === 'lane' ? 'Cambiar fecha, hora o lane' : 'Cambiar fecha/hora')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">{duplicateMode ? 'Reservación a duplicar (se mantiene)' : 'Reservación actual'}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {r.categoryName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.date} · {formatTime12(r.startTime)} – {formatTime12(r.endTime)}
                {r.laneName ? ` · ${r.laneName}` : ''}
              </Typography>
            </Box>

            {rescheduleError && <Alert severity="error">{rescheduleError}</Alert>}

            {!duplicateMode && reservation?.categoryType === 'lane' && laneCategories.length > 1 && (
              <TextField
                select
                size="small"
                label="Tipo de lane (pistola, rifle…)"
                value={targetCategoryId}
                onChange={(e) => { setNewCategoryId(e.target.value === reservation.categoryId ? '' : e.target.value); setNewStartTime(''); setNewLaneId(''); }}
                helperText={newCategoryId && newCategoryId !== reservation.categoryId ? 'Se mueve a otra categoría de lane. Lo ya cobrado no cambia; si el precio es distinto, cóbralo en tienda.' : 'Puedes mover la reservación a un lane de otra categoría'}
              >
                {laneCategories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Fechas disponibles
              </Typography>
              {datesLoading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Buscando fechas...</Typography>
                </Stack>
              ) : availableDates.length === 0 ? (
                <Alert severity="warning">
                  Esta categoría no tiene horarios recurrentes configurados.
                </Alert>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {availableDates.map((d) => (
                    <Button
                      key={d.iso}
                      size="small"
                      variant={newDate === d.iso ? 'contained' : 'outlined'}
                      onClick={() => { setNewDate(d.iso); setNewStartTime(''); }}
                      sx={{ minWidth: 104, borderRadius: 2 }}
                    >
                      {d.label}
                    </Button>
                  ))}
                </Stack>
              )}
              {/* Fecha más allá de las próximas listadas (pedido 2026-09-15:
                  "solo me deja hasta el 15 de octubre") */}
              <TextField
                type="date"
                size="small"
                label="Otra fecha (calendario)"
                value={newDate && !availableDates.some((d) => d.iso === newDate) ? newDate : ''}
                onChange={(e) => { if (e.target.value) { setNewDate(e.target.value); setNewStartTime(''); } }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                sx={{ mt: 1.5, minWidth: 220 }}
                helperText="Si la fecha no está en la lista, escógela aquí"
              />
            </Box>

            {newDate && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Horas disponibles
                </Typography>
                {newSlotsLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Buscando horarios...</Typography>
                  </Stack>
                ) : newSlots.length === 0 ? (
                  <Alert severity="warning">
                    No hay horarios disponibles para esta categoría en esta fecha.
                  </Alert>
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {newSlots.map((s) => {
                      const isSelected = newStartTime === s.startTime;
                      // En cursos el ADMIN puede escoger horarios llenos
                      // (sobre-cupo permitido); en lanes no — el lane es físico.
                      const isFull = s.available === false;
                      const disabled = isFull && reservation?.categoryType === 'lane';
                      return (
                        <Button
                          key={s.startTime}
                          size="small"
                          disabled={disabled}
                          variant={isSelected ? 'contained' : 'outlined'}
                          color={isFull && !disabled ? 'warning' : 'primary'}
                          onClick={() => setNewStartTime(s.startTime)}
                          sx={{ minWidth: 90, borderRadius: 2 }}
                        >
                          {formatTime12(s.startTime)}{isFull && !disabled ? ' (lleno)' : ''}
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {newStartTime && reservation?.categoryType === 'lane' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Lane
                </Typography>
                {laneError && <Alert severity="error" sx={{ mb: 1 }}>{laneError}</Alert>}
                {r.guest?.laneName && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    El acompañante tiene su propio lane ({r.guest.laneName}); este cambio
                    solo mueve el lane principal.
                  </Alert>
                )}
                {lanesLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Buscando lanes...</Typography>
                  </Stack>
                ) : laneOptions.length === 0 ? (
                  !laneError && <Alert severity="warning">No hay lanes configurados para esta categoría.</Alert>
                ) : (
                  <>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {laneOptions.map((l) => {
                        const isCurrent = l.id === r.laneId;
                        const isSelected = newLaneId === l.id;
                        const busy = l.available === false;
                        return (
                          <Button
                            key={l.id}
                            size="small"
                            disabled={busy}
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => setNewLaneId(l.id)}
                            sx={{ minWidth: 96, borderRadius: 2 }}
                          >
                            {l.name}
                            {isCurrent ? ' (actual)' : ''}
                            {busy ? ' (ocupado)' : ''}
                          </Button>
                        );
                      })}
                    </Stack>
                    {laneOptions.some((l) => l.id === r.laneId && l.available === false) && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                        {duplicateMode
                          ? (newLaneId
                            ? `El lane actual (${r.laneName}) está ocupado a esa hora — se asignó ${laneOptions.find((l) => l.id === newLaneId)?.name || 'otro lane'} automáticamente.`
                            : 'No hay ningún lane libre a esa hora — escoge otra hora.')
                          : `El lane actual (${r.laneName}) no está libre en ese horario — escoge otro lane.`}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}

            {newStartTime && (reservation?.categoryType !== 'lane' || newLaneId) && (
              <Alert severity="info">
                {duplicateMode ? 'Nueva reservación para ' : 'Mover a: '}
                {duplicateMode && <strong>{r.userName}</strong>}
                {duplicateMode ? ' el ' : ''}
                <strong>{newDate}</strong> · <strong>{formatTime12(newStartTime)}</strong>
                {reservation?.categoryType === 'lane' && newLaneId && (
                  <> · <strong>{laneOptions.find((l) => l.id === newLaneId)?.name}</strong></>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReschedule}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={duplicateMode ? handleDuplicate : handleReschedule}
            disabled={actionLoading || !newStartTime || (reservation?.categoryType === 'lane' && !newLaneId)}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading
              ? (duplicateMode ? 'Duplicando...' : 'Reagendando...')
              : (duplicateMode ? 'Crear duplicado' : 'Confirmar cambio')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editar acompañante */}
      <Dialog open={guestDialogOpen} onClose={() => setGuestDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar acompañante</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {guestError && <Alert severity="error">{guestError}</Alert>}
            <TextField
              label="Nombre"
              value={guestForm.name}
              onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Teléfono"
              value={guestForm.phone}
              onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
              fullWidth
              size="small"
              placeholder="787-555-1234"
            />
            <TextField
              label="Email"
              value={guestForm.email}
              onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Licencia"
              value={guestForm.licenseNumber}
              onChange={(e) => setGuestForm({ ...guestForm, licenseNumber: e.target.value })}
              fullWidth
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Solo se cambian los datos de contacto. El lane y lo que se cobró no cambian.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="error" onClick={handleRemoveGuest} disabled={actionLoading} startIcon={<Iconify icon="eva:trash-2-outline" />}>
            Quitar acompañante
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setGuestDialogOpen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSaveGuest}
              disabled={actionLoading || !guestForm.name.trim()}
              startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            >
              Guardar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
